import { CopySettings, WebflowStyleGuide } from 'base/copy_settings';
import { getLocalStorageItem, listenToStorageEvent } from 'base/localstorage';
import { menuDivider, menuItem, subMenu } from 'base/ui/menu_model';
import {
  getProjectCopySettings,
  setProjectCopySettings,
} from 'blocks/copy_settings';
import { makeObservable, observable } from 'mobx';
import { exportFormatMenuItems } from './export_and_share';
import { Project } from './model';

export const getPreferencesMenu = (project: Project) => {
  return [
    menuItem({
      label: 'Invert zoom direction',
      onClick: () =>
        invertZoomDirectionPreference.set(!invertZoomDirectionPreference.get()),
      checked: invertZoomDirectionPreference.get(),
    }),
    subMenu({
      label: 'Export format',
      menu: exportFormatMenuItems.get(),
    }),
    subMenu({
      label: 'Webflow Export',
      menu: getWebflowExportPreferencesMenu(project),
    }),
  ];
};
const getWebflowExportPreferencesMenu = (project: Project) => {
  const value = getProjectCopySettings(project);
  const update = (settings: Partial<CopySettings>) => {
    setProjectCopySettings(project, { ...value, ...settings });
  };

  return [
    menuItem({
      label: 'Relume Style Guide V3 - Client-First V2.1 (recommended)',
      tooltip: 'Relume Style Guide V3 - Client-First V2.1 (recommended)',
      checked: value.style === WebflowStyleGuide.RelumeV3_CF2_1,
      onClick: () => update({ style: WebflowStyleGuide.RelumeV3_CF2_1 }),
    }),
    menuItem({
      label: 'Relume Style Guide V2 - Client-First V2.1',
      tooltip: 'Relume Style Guide V2 - Client-First V2.1',
      checked: value.style === WebflowStyleGuide.RelumeV2_CF2_1,
      onClick: () => update({ style: WebflowStyleGuide.RelumeV2_CF2_1 }),
    }),
    menuItem({
      label: 'Relume Style Guide V1.5 - Client-First V2',
      tooltip: 'Relume Style Guide V1.5 - Client-First V2',
      checked: value.style === WebflowStyleGuide.RelumeV1_5_CF2,
      onClick: () => update({ style: WebflowStyleGuide.RelumeV1_5_CF2 }),
    }),
    menuItem({
      label: 'Finsweet Style Guide - CF V2.1',
      tooltip: 'Finsweet Style Guide - Client-First V2.1',
      checked: value.style === WebflowStyleGuide.Finsweet_CF2_1,
      onClick: () => update({ style: WebflowStyleGuide.Finsweet_CF2_1 }),
    }),
    menuItem({
      label: 'Finsweet Style Guide - CF V2',
      tooltip: 'Finsweet Style Guide - Client-First V2',
      checked: value.style === WebflowStyleGuide.Finsweet_CF2,
      onClick: () => update({ style: WebflowStyleGuide.Finsweet_CF2 }),
    }),
    menuDivider(),
    menuItem({
      label: 'Spacing Wrappers',
      checked: value.spacing === 'wrappers',
      onClick: () => update({ spacing: 'wrappers' }),
    }),
    menuItem({
      label: 'Spacing Blocks',
      checked: value.spacing === 'blocks',
      onClick: () => update({ spacing: 'blocks' }),
    }),
  ];
};

class LocalStoragePreference {
  @observable.ref private value: boolean;

  constructor(private readonly key: string) {
    this.value = this.getFromStorage();
    makeObservable(this);
    listenToStorageEvent(() => {
      this.value = this.getFromStorage();
    });
  }

  private getFromStorage() {
    return getLocalStorageItem(this.key) === 'true';
  }

  set(value: boolean) {
    this.value = value;
    localStorage.setItem(this.key, JSON.stringify(value));
  }

  get() {
    return this.value;
  }
}

export const invertZoomDirectionPreference = new LocalStoragePreference(
  'rlInvertZoomDirection',
);
